import React, {useState, useEffect, useRef} from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter} from 'react-router-dom';
import { connect } from 'react-redux';

import {SET_LOADING_SPINNER_ENABLE, SET_LOADING_OVERLAY_ENABLE} from 'actions/types';
import DatabaseService from 'services/DatabaseService.js'
import {Button, Tab} from 'semantic-ui-react';
import {colors, fontSizes, fonts, spacings, shadow, buttons} from 'style';
import {setSidebarEnable} from 'actions/sidebar.js';
import {List} from 'components/list'
import InvitationUploadForm from './InvitationUploadForm'

import {getInvitations} from 'reducers/invitations';
import {fetchInvitations} from 'actions/invitations';
import {Buffer} from "buffer"

const formatAssignedList = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v === 'string' || typeof v === 'number') return String(v)
            if (typeof v !== 'object') return ''
            return (
                v.name ??
                v.label ??
                v.title ??
                v.code ??
                v.key ??
                v.id ??
                ''
            )
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    if (parts.length) return parts.join(', ')
    if (Array.isArray(value)) return `${value.length}`
    return ''
}

const formatInvitationEmployeeAttributes = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            const name =
                v?.employeeAttribute?.label ??
                v?.employeeAttribute?.name ??
                v?.employeeAttributeId ??
                v?.id ??
                ''
            const val = v?.data
            if (!name) return ''
            if (val === undefined || val === null || val === '') return ''
            return `${name}: ${val}`
        })
        .filter((s) => s && String(s).trim().length)

    return parts.join('\n')
}

const formatInvitationSegments = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            return (
                v?.team?.name ??
                v?.team?.lc_name ??
                v?.teamId ??
                v?.id ??
                ''
            )
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    return parts.join('\n')
}

const formatInvitationRewards = (value) => {
    if (!value) return ''
    const list = Array.isArray(value) ? value : [value]
    const parts = list
        .flat()
        .filter(Boolean)
        .map((v) => {
            if (typeof v !== 'object') return String(v ?? '').trim()
            const lines = []
            const stars = Number(v?.rewardPoints ?? 0)
            const gems = Number(v?.rewardResources ?? 0)
            if (Number.isFinite(stars) && stars > 0) lines.push(`Stars: ${stars}`)
            if (Number.isFinite(gems) && gems > 0) lines.push(`Gems: ${gems}`)
            return lines.join('\n')
        })
        .filter((s) => s && String(s).trim().length)
        .map((s) => String(s).trim())

    // If there are multiple rewards (rare), keep them separated
    return parts.join('\n')
}

const getInvitationEmployeeAttributes = (invitation) =>
    invitation?.invitationEmployeeAttributes ??
    invitation?.employeeAttributes ??
    invitation?.employee_attributes

const getInvitationSegments = (invitation) =>
    invitation?.invitationSegments ??
    invitation?.segments

const getInvitationRewards = (invitation) =>
    invitation?.invitationRewards ??
    invitation?.rewards

const styles = {
    content: {
        fontSize: fontSizes.body,
        lineHeight: '1.5',
        minHeight: '100%',
        padding: spacings.base,
        backgroundColor: colors.themeGrey,
    },
    topPanel: {
        display: 'flex',
        marginBottom: 16
    }
}

const invitationFields = [
    {
        name: 'Code',
        key: 'code',
        default: true,
        searchable: true
    },
    {
        name: 'Uses',
        key: 'uses',
        default: true
    },
    {
        name: 'Employee Attributes',
        key: 'invitationEmployeeAttributes',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationEmployeeAttributes(getInvitationEmployeeAttributes(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Segments',
        key: 'invitationSegments',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationSegments(getInvitationSegments(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Rewards',
        key: 'invitationRewards',
        default: true,
        sortable: false,
        type: 'custom',
        valueFunc: (_value, invitation) => (
            <div style={{whiteSpace: 'pre-line'}}>
                {formatInvitationRewards(getInvitationRewards(invitation))}
            </div>
        ),
        cellStyle: {maxWidth: 260}
    },
    {
        name: 'Created At',
        key: 'createdAt',
        default: true,
        defaultSort: true,
        defaultSortOrder: 'descending',
        type: 'date'
    },
    {
        name: 'Link',
        key: 'link'
    },
    {
        name: 'Single Use',
        key: 'singleUse'
    }
]

const InvitationsContainer = ({t, dispatch, fetchInvitations}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [update, setUpdate] = useState()
    const [invitationModal, setInvitationModal] = useState()
    const [response, setResponse] = useState() 
    const [error, setError] = useState()

    useEffect(() => {
        dispatch(setSidebarEnable(true))
    }, [])

    const fetchPublicInvitations = (params) => {
        params = {
            ...params,
            public: true
        }
        return fetchInvitations(params)
    }

    const fetchSingleUserInvitations = (params) => {
        params = {
            ...params,
            singleUse: true
        }
        return fetchInvitations(params)
    }

    const uploadInvitation = async (event, type) => {
        var file = event.target.files[0];
        event.target.value=null;

        var reader = new FileReader();
        reader.onerror= (e) => { window.alert(e)  };
        reader.onload = async (e) => {
            dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:true});
            switch(type) {
                case 'reward':
                    var result = await DatabaseService.postInvitationRewards({
                        invitationRewards: {csv: {base64: Buffer.from(reader.result).toString('base64')}}
                    })
                    .catch((error)=> {
                        console.error(error)
                        setError(error)
                    })
                    if (result) {
                        setResponse(result)
                        setError()
                    }
                    break
                case 'invitation':
                    var result = await DatabaseService.postInvitations({
                        invitations: {csv: {base64: Buffer.from(reader.result).toString('base64')}},
                        overwrite: true
                    })
                    .catch((error)=> {
                        console.error(error)
                        setError(error)
                    })
                    if (result) {
                        setResponse(result)
                        setError()
                        setUpdate(!update)
                    }
                    break
            }
            dispatch({type: SET_LOADING_OVERLAY_ENABLE, enable:false});
        }
        reader.readAsText(file, 'UTF-8');
    }

    const openInvitationModal = () => {
        setInvitationModal(true)
    }

    const cancelModal = () => {
        setInvitationModal(false)
        setResponse()
        setError()
    }

    const panes = [
        {
          menuItem: t('Public'),
          pane: {
            content: (
                <Tab.Pane>
                    {activeIndex === 0 && (
                        <List
                            listKey="invitations-public"
                            getData={fetchPublicInvitations}
                            searchData={fetchPublicInvitations}
                            update={update}
                            type='table'
                            actions={[
                            ]}
                            fields={invitationFields}
                        />
                    )}
                </Tab.Pane>
            )
          }
        },
        {
          menuItem: t('Single Use'),
          pane: {
            content: (
                <Tab.Pane>
                    {activeIndex === 1 && (
                        <List
                            listKey="invitations-single-use"
                            getData={fetchSingleUserInvitations}
                            searchData={fetchSingleUserInvitations}
                            update={update}
                            type='table'
                            actions={[
                            ]}
                            fields={invitationFields}
                        />
                    )}
                </Tab.Pane>
            )
          }
        },
        {
            menuItem: t('All Invitations'),
            pane: {
                content: (
                    <Tab.Pane>
                        {activeIndex === 2 && (
                            <List
                                listKey="invitations-all"
                                getData={fetchInvitations}
                                searchData={fetchInvitations}
                                update={update}
                                type='table'
                                fields={invitationFields}
                            />
                        )}
                    </Tab.Pane>
                )
            }
        }
    ]

    return (
        <div style={styles.content}>
            <div style={styles.topPanel}>
                <div>
                    <h2>{t('Add')}</h2>
                    <Button onClick={openInvitationModal}>{t('Add Invitation')}</Button>
                </div>
            </div>
            <Tab
                panes={panes}
                renderActiveOnly={false}
                activeIndex={activeIndex}
                onTabChange={(_e, data) => setActiveIndex(Number(data.activeIndex) || 0)}
            />
            <InvitationUploadForm
                open={invitationModal}
                close={cancelModal}
                upload={uploadInvitation}
                response={response}
                error={error}
            />
        </div>
    )
}

const mapStateToProps = (state, ownProps) => {
    return {invitations: getInvitations(state.invitations)};
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchInvitations: (params, force) => {
            return dispatch(fetchInvitations(params, force))
        },
        dispatch: dispatch
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation('invitations')(InvitationsContainer)));